const _pendingSizeSelection = new WeakMap();
const _applyStateFrame = new WeakMap();
const EMPTY_SIZE_LABEL = '—';

class ProductInfo extends HTMLElement {
  onVariantChangeUnsubscriber = undefined;
  cartUpdateUnsubscriber = undefined;
  abortController = undefined;
  pendingRequestUrl = null;
  preProcessHtmlCallbacks = [];
  postProcessHtmlCallbacks = [];

  constructor() {
    super();
    this.scrollsnap = this.querySelector('scroll-snap');
  }

  connectedCallback() {
    this.initializeProductSwapUtility();

    this.onVariantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    );

    this.scheduleApplyState();
    this.initColorSwatchSizePointing();
    this.initSizeChangeTracking();
    this.updateNostoPrices();

    this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
  }

  addPreProcessCallback(callback) {
    this.preProcessHtmlCallbacks.push(callback);
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
    this.cartUpdateUnsubscriber?.();
  }

  initializeProductSwapUtility() {
    this.postProcessHtmlCallbacks.push((newNode) => {
      window?.Shopify?.PaymentButton?.init();

      if (window.yotpoWidgetsContainer?.initWidgets) {
        const newProductId = newNode.dataset.productId;
        if (newProductId) {
          const productData = this._swapProductData;
          const newUrl = productData?.url ? `${window.location.origin}${productData.url}` : '';
          const newName = productData?.title || '';
          const newPrice = productData?.price_amount || '';
          const newImage = productData?.featured_image || '';
          const newDescription = productData?.description_plain || '';

          document.querySelectorAll(`#main-content [data-yotpo-product-id]:not([data-yotpo-product-id="${newProductId}"])`).forEach((el) => {
            el.setAttribute('data-yotpo-product-id', newProductId);
            el.setAttribute('data-yotpo-name', newName);
            el.setAttribute('data-yotpo-url', newUrl);
            el.setAttribute('data-yotpo-price', newPrice);
            el.setAttribute('data-yotpo-image-url', newImage);
            el.setAttribute('data-yotpo-description', newDescription);
          });
        }
        window.yotpoWidgetsContainer.initWidgets();
      }

      if (typeof enableZoomOnHover === 'function') {
        enableZoomOnHover(2.5);
      }

      if (typeof window.initPhotoSwipe === 'function') {
        window.initPhotoSwipe();
      }

      if (window._swat && newNode.id) {
        window._swat.initializeActionButtons(`#${newNode.id}`);
      }

      if (window.nostojs) {
        const nostoProductId = newNode.dataset.productId;
        if (nostoProductId) {
          window.nostojs((api) => {
            api.defaultSession()
              .setResponseMode('HTML')
              .enableCampaignInjection()
              .viewProduct(nostoProductId)
              .setPlacements(api.placements.getPlacements())
              .update();
          });
        }
      }
    });
  }

  // -----------------------------------------------------------------------
  //  Size selection (static utilities)
  // -----------------------------------------------------------------------

  static getFamilyMap(productInfo) {
    const script = productInfo?.querySelector('[data-product-color-family-option-map]');
    if (!script) return null;
    const text = script.textContent.trim();
    if (!text) return null;
    try { return JSON.parse(text)?.optionMap ?? null; } catch { return null; }
  }

  static getLandedEntry(productInfo) {
    const map = ProductInfo.getFamilyMap(productInfo);
    if (!map) return null;
    return Object.values(map).find((e) => e.isLandedProduct) ?? null;
  }

  static getEntryByHandle(productInfo, handle) {
    const map = ProductInfo.getFamilyMap(productInfo);
    if (!map || !handle) return null;
    return Object.values(map).find((e) => e.handle === handle) ?? null;
  }

  static getSizeOptionKey(entry) {
    if (!entry) return null;
    for (const key of Object.keys(entry)) {
      if (typeof entry[key] === 'object' && entry[key] !== null && key.toLowerCase().includes('size')) {
        return key;
      }
    }
    return null;
  }

  static getSizeValues(entry) {
    const key = ProductInfo.getSizeOptionKey(entry);
    return key ? entry[key].values ?? null : null;
  }

  static getSizeFieldset(productInfo) {
    if (!productInfo) return null;
    const entry = ProductInfo.getLandedEntry(productInfo);
    const sizeKey = ProductInfo.getSizeOptionKey(entry);
    const sizeOption = sizeKey ? entry[sizeKey] : null;
    if (!sizeOption) return null;
    const fieldsets = productInfo.querySelectorAll('variant-selects fieldset:not([data-color-family])');
    return fieldsets[sizeOption.position - 1] || null;
  }

  static getSizeInput(fieldset, sizeValue) {
    if (!fieldset || !sizeValue) return null;
    return fieldset.querySelector(`input[type="radio"][value="${CSS.escape(sizeValue)}"]`);
  }

  static getSelectedSizeValue(productInfo) {
    const fieldset = ProductInfo.getSizeFieldset(productInfo);
    if (!fieldset) return null;
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    return checked?.value ?? null;
  }

  static resolveSelectedVariant(productInfo) {
    const selectedSize = ProductInfo.getSelectedSizeValue(productInfo);
    if (!selectedSize) return null;
    const values = ProductInfo.getSizeValues(ProductInfo.getLandedEntry(productInfo));
    return values?.[selectedSize] ?? null;
  }

  static setSizeLabel(productInfo, value) {
    const label = ProductInfo.getSizeFieldset(productInfo)?.querySelector('[data-selected-value]');
    if (label) label.textContent = value;
  }

  // Uncheck the size radios and reset the label. Used when the active size is
  // not available/offered in the current product (e.g. after a colour swap),
  // so the form falls back to the "please select size" state instead of
  // showing a stale or sold-out selection.
  static clearSizeSelection(productInfo) {
    const fieldset = ProductInfo.getSizeFieldset(productInfo);
    if (!fieldset) return;
    fieldset.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      input.checked = false;
    });
    ProductInfo.setSizeLabel(productInfo, EMPTY_SIZE_LABEL);
  }

  static setPendingSizeSelection(productInfo, sizeValue) {
    if (!productInfo || !sizeValue) return;
    _pendingSizeSelection.set(productInfo, sizeValue);
  }

  static reapplySizeAfterVariantUpdate(productInfo) {
    const sizeValue = _pendingSizeSelection.get(productInfo);
    if (!sizeValue) return;
    const fieldset = ProductInfo.getSizeFieldset(productInfo);
    const input = ProductInfo.getSizeInput(fieldset, sizeValue);
    if (!input) {
      ProductInfo.setSizeLabel(productInfo, EMPTY_SIZE_LABEL);
      return;
    }
    input.checked = true;
    ProductInfo.setSizeLabel(productInfo, sizeValue);
  }

  static setNotifyVisible(productForm, visible) {
    const notifyButton = productForm?.querySelector('.product__notify-button');
    if (!notifyButton) return;
    notifyButton.classList.toggle('hidden', !visible);
  }

  static setPleaseSelectPurchaseState(productForm) {
    if (!productForm) return;
    const submitButton = productForm.querySelector('[type="submit"]');
    submitButton?.removeAttribute('data-soldout');
    submitButton?.removeAttribute('data-preorder-depleted');
    ProductInfo.setNotifyVisible(productForm, false);
    productForm.toggleSubmitButton(true, window.variantStrings.pleaseSelectSize);
  }

  static enablePurchase(productForm) {
    const submitButton = productForm.querySelector('[type="submit"]');
    const isPreorder = submitButton?.hasAttribute('data-preorder');
    if (isPreorder) {
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('data-soldout');
      submitButton.removeAttribute('data-please-select-size');
      const span = submitButton.querySelector('span');
      if (span) span.textContent = window.variantStrings.preOrder;
    } else {
      productForm.toggleSubmitButton(false);
    }
    ProductInfo.setNotifyVisible(productForm, false);
  }

  static syncSubmitButton(productInfo) {
    const productForm = productInfo?.querySelector('product-form');
    if (!productForm) return;

    const sizeFieldset = ProductInfo.getSizeFieldset(productInfo);
    if (!sizeFieldset) {
      ProductInfo.syncSubmitButtonFromServerState(productInfo, productForm);
      return;
    }

    const selectedSize = ProductInfo.getSelectedSizeValue(productInfo);
    const variantIdInput = productForm.querySelector('input[name="id"]');

    if (!selectedSize) {
      if (variantIdInput) {
        variantIdInput.value = '';
        variantIdInput.disabled = true;
      }
      ProductInfo.setPleaseSelectPurchaseState(productForm);
      return;
    }

    const variant = ProductInfo.resolveSelectedVariant(productInfo);

    // Size is selected but the product doesn't offer it at all (no mapped
    // variant) — happens when a colour swap carries over a size the new product
    // lacks. Clear it and fall back to "please select size" rather than leaving a
    // stale button for a phantom variant.
    if (!variant) {
      if (variantIdInput) {
        variantIdInput.value = '';
        variantIdInput.disabled = true;
      }
      ProductInfo.clearSizeSelection(productInfo);
      ProductInfo.setPleaseSelectPurchaseState(productForm);
      return;
    }

    if (variantIdInput) {
      variantIdInput.value = variant.variantId;
      variantIdInput.disabled = !variant.available;
    }

    // Size exists but is sold out in this product: keep it selected and offer the
    // Sold Out + notify-me (restock) affordance.
    if (!variant.available) {
      productForm.toggleSubmitButton(true, window.variantStrings.soldOut);
      ProductInfo.setNotifyVisible(productForm, true);
      return;
    }

    ProductInfo.enablePurchase(productForm);
  }

  static syncSubmitButtonFromServerState(productInfo, productForm) {
    const entry = ProductInfo.getLandedEntry(productInfo);
    if (!entry) return;

    const variant = ProductInfo.resolveDefaultVariant(entry);
    if (!variant) return;

    const variantIdInput = productForm.querySelector('input[name="id"]');
    if (variantIdInput) {
      variantIdInput.value = variant.variantId;
      variantIdInput.disabled = !variant.available;
    }

    if (!variant.available) {
      productForm.toggleSubmitButton(true, window.variantStrings.soldOut);
      ProductInfo.setNotifyVisible(productForm, true);
      return;
    }

    ProductInfo.enablePurchase(productForm);
  }

  static resolveDefaultVariant(entry) {
    if (!entry) return null;
    for (const key of Object.keys(entry)) {
      if (typeof entry[key] === 'object' && entry[key] !== null && entry[key].values) {
        const values = entry[key].values;
        const firstValue = Object.values(values)[0];
        if (firstValue?.variantId) return firstValue;
      }
    }
    return null;
  }

  // -----------------------------------------------------------------------
  //  Size selection (instance)
  // -----------------------------------------------------------------------

  scheduleApplyState() {
    const pending = _applyStateFrame.get(this);
    if (pending) cancelAnimationFrame(pending);

    const frame = requestAnimationFrame(() => {
      _applyStateFrame.delete(this);
      this.applyInitialPurchaseState();
    });
    _applyStateFrame.set(this, frame);
  }

  applyInitialPurchaseState() {
    if (!ProductInfo.getSizeFieldset(this)) {
      ProductInfo.syncSubmitButton(this);
      return;
    }

    const selectedSize = ProductInfo.getSelectedSizeValue(this);
    if (!selectedSize) {
      ProductInfo.setPleaseSelectPurchaseState(this.productForm);
      return;
    }
    ProductInfo.setSizeLabel(this, selectedSize);
    ProductInfo.syncSubmitButton(this);
  }

  initSizeChangeTracking() {
    this.addEventListener('change', (e) => {
      if (e.target.type !== 'radio') return;
      const fieldset = e.target.closest('fieldset');
      if (!fieldset || !fieldset.closest('variant-selects')) return;
      if (fieldset !== ProductInfo.getSizeFieldset(this)) return;
      ProductInfo.setPendingSizeSelection(this, e.target.value);
    });
  }

  // -----------------------------------------------------------------------
  //  Color swatch size-pointing
  // -----------------------------------------------------------------------

  initColorSwatchSizePointing() {
    if (this.dataset.updateUrl !== 'true') return;
    if (!this.querySelectorAll('.product-form__color-item a[data-handle]').length) return;

    const pointLinks = () => {
      const selectedSize = ProductInfo.getSelectedSizeValue(this);
      this.querySelectorAll('.product-form__color-item a[data-handle]').forEach((link) => {
        const entry = ProductInfo.getEntryByHandle(this, link.dataset.handle);
        const baseUrl = entry?.url || `/products/${link.dataset.handle}`;
        const sizeValues = ProductInfo.getSizeValues(entry);
        const target = selectedSize ? sizeValues?.[selectedSize] : null;
        link.href = (target?.available && target.variantUrl) ? target.variantUrl : baseUrl;
      });
    };

    this.addEventListener('change', (e) => {
      if (e.target.type === 'radio' && e.target.closest('variant-selects')) {
        pointLinks();
      }
    });
    requestAnimationFrame(() => requestAnimationFrame(pointLinks));
  }

  // -----------------------------------------------------------------------
  //  Variant / product swap
  // -----------------------------------------------------------------------

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    const productUrl = target.dataset.productUrl || this.dataset.url;
    // Compare product identity by PATHNAME, not the full URL: a size option's
    // data-product-url carries a ?variant= query, so a raw string compare against
    // the bare data-url falsely reads as a product swap (and, after a color swap,
    // makes the first size tap kick off a needless re-fetch — the "two taps to
    // select" bug). Only a different pathname means a genuinely different product.
    const shouldSwapProduct = ProductInfo.productPath(productUrl) !== ProductInfo.productPath(this.dataset.url);

    if (shouldSwapProduct && this.dataset.colorSwatchRedirect === 'true') {
      window.location.href = productUrl;
      return;
    }

    // Product swap (color): fetch immediately — it's a navigation-like change
    // and network latency already defers the DOM swap past the tap gesture.
    if (shouldSwapProduct) {
      this.pendingRequestUrl = productUrl;
      // Remember the active size so we can re-apply it client-side after the swap.
      // The server only pre-selects a size when the request carries ?variant=,
      // which buildSwapRequestUrl produces inconsistently — so size persistence
      // can't rely on the rendered HTML. Carry it ourselves for deterministic
      // behaviour (re-applied only if available in the new product; else cleared).
      this._carrySize = ProductInfo.getSelectedSizeValue(this);
      this.resetProductFormState();
      // Mark the product area as swapping for the duration of the fetch so it can
      // show a subtle loading state. The flag lives on this (outgoing) node, which
      // stays mounted until the response swaps it out — so the new, clean node
      // renders without it; no explicit clear needed.
      this.setAttribute('data-swapping', '');
      this.renderProductInfo({
        requestUrl: this.buildSwapRequestUrl(productUrl, target.value),
        targetId: target.id,
        callback: this.handleSwapProduct(productUrl),
      });
      return;
    }

    // Re-tapping the ALREADY-active colour swatch is a no-op: same product, and
    // it's a colour-family radio (not a size). Without this guard the same-product
    // path runs, reads the (currently unselected) size as null, and wipes the
    // selection — clearing the swatch + size the user didn't intend to change.
    if (event.target.closest('[data-color-family]')) return;

    // Same-product option change (e.g. size): everything we need is SSR'd into
    // the page (variant map + pre-rendered price/SKU fragments), so update the
    // UI entirely client-side with no fetch. This keeps the form in lock-step
    // with rapid tapping — no abort-storm, no disabled-button lag.
    this.updateForSameProductVariant(productUrl);
  }

  // Pathname of a product URL, stripped of query (?variant=) and origin, for
  // comparing product identity. Falls back to a manual split if URL parsing
  // throws (e.g. unexpected input).
  static productPath(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return String(url).split('?')[0];
    }
  }

  updateForSameProductVariant(productUrl) {
    const variant = ProductInfo.resolveSelectedVariant(this);

    this.applyVariantRender(variant?.variantId);
    // Pass the bare product path: productUrl may already carry a ?variant= query
    // (size inputs do), and updateURL appends its own ?variant=, which would
    // otherwise produce a doubled query string.
    this.updateURL(ProductInfo.productPath(productUrl), variant?.variantId);
    // syncSubmitButton runs last: it owns the final variant-id input value and
    // its disabled (sold-out) state, so it must not be overwritten afterwards.
    this.updateVariantInputs(variant?.variantId);
    ProductInfo.syncSubmitButton(this);

    if (variant?.variantId != null) {
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          variant: { id: variant.variantId },
        },
      });
    }
  }

  static getVariantRenderMap(productInfo) {
    const script = productInfo?.querySelector('[data-variant-render-map]');
    if (!script) return null;
    const text = script.textContent.trim();
    if (!text) return null;
    try { return JSON.parse(text); } catch { return null; }
  }

  applyVariantRender(variantId) {
    const map = ProductInfo.getVariantRenderMap(this);
    const entry = variantId != null ? map?.[String(variantId)] : null;
    if (!entry) return;

    const priceEl = this.querySelector(`#price-${this.dataset.section}`);
    if (priceEl && typeof entry.priceHtml === 'string') {
      priceEl.innerHTML = entry.priceHtml;
      priceEl.classList.remove('hidden');
    }

    const skuEl = this.querySelector(`#Sku-${this.dataset.section}`);
    if (skuEl && typeof entry.skuHtml === 'string') {
      skuEl.innerHTML = entry.skuHtml;
    }
  }

  resetProductFormState() {
    const productForm = this.productForm;
    productForm?.toggleSubmitButton(true);
    productForm?.handleErrorMessage();
  }

  handleSwapProduct(productUrl) {
    return (html) => {
      this.productModal?.remove();

      const newProductInfo = html.querySelector('product-info');
      const variant = this.getSelectedVariant(newProductInfo);

      const displayUrl = variant?.id ? `${productUrl}?variant=${variant.id}` : productUrl;
      window.history.replaceState({}, '', displayUrl);

      const newTitle = newProductInfo?.querySelector('.product__title')?.textContent?.trim();
      if (newTitle) document.title = newTitle;

      const productJsonEl = html.querySelector('[id^="ProductJson-"]');
      try { this._swapProductData = productJsonEl ? JSON.parse(productJsonEl.textContent) : null; } catch { this._swapProductData = null; }

      HTMLUpdateUtility.viewTransition(
        this,
        newProductInfo,
        this.preProcessHtmlCallbacks,
        this.postProcessHtmlCallbacks
      );

      this._swapProductData = null;

      // Re-create the swapped-in option inputs as fresh live nodes. Inputs moved
      // into the DOM via innerHTML are treated by iOS Safari as non-interactive
      // for the first gesture, so the first tap after a swap registers touchend
      // but no synthesized click (needs a second tap). Replacing each radio with
      // a clone yields freshly-constructed elements that iOS treats as tappable
      // immediately, so the first post-swap tap fires its click.
      ProductInfo.refreshOptionInputs();

      // Deterministically carry the pre-swap size into the new product (the
      // server's pre-selection is unreliable). Re-apply only if that size is
      // available here; otherwise leave it cleared (→ please-select).
      ProductInfo.reapplyCarriedSize(this._carrySize);
      this._carrySize = null;
    };
  }

  static reapplyCarriedSize(sizeValue) {
    const live = document.querySelector('product-info[id^="MainProduct"]');
    if (!live || !sizeValue) {
      if (live) ProductInfo.syncSubmitButton(live);
      return;
    }
    const values = ProductInfo.getSizeValues(ProductInfo.getLandedEntry(live));
    const target = values?.[sizeValue];
    if (target?.available) {
      const fieldset = ProductInfo.getSizeFieldset(live);
      const input = ProductInfo.getSizeInput(fieldset, sizeValue);
      if (input) {
        input.checked = true;
        ProductInfo.setSizeLabel(live, sizeValue);
      }
    }
    // Re-sync the button to the (possibly newly re-applied) size state.
    ProductInfo.syncSubmitButton(live);
  }

  static refreshOptionInputs() {
    const live = document.querySelector('product-info[id^="MainProduct"]');
    const inputs = live?.querySelectorAll('variant-selects input[type="radio"]');
    if (!inputs?.length) return;
    // Capture which inputs are checked BEFORE mutating. Use the `checked`
    // ATTRIBUTE as the source of truth, not the `.checked` property: these inputs
    // were just parsed via innerHTML on a detached node and moved into the DOM,
    // and in that path the live `.checked` property is unreliable (often false
    // even when the server rendered the attribute) — reading it here was wiping
    // the swapped-to colour's selected state. The attribute survives parsing.
    // Replacing radios one by one in a shared name group also lets the browser's
    // group-exclusivity uncheck a sibling we already set, so restore in a second
    // pass after all clones are in the DOM.
    const wasChecked = new WeakMap();
    const fresh = [];
    inputs.forEach((input) => {
      const clone = input.cloneNode(true);
      const shouldCheck = input.hasAttribute('checked') || input.checked;
      clone.checked = false; // set later, after all are in the DOM
      wasChecked.set(clone, shouldCheck);
      input.replaceWith(clone);
      fresh.push(clone);
    });
    fresh.forEach((clone) => {
      if (wasChecked.get(clone)) {
        clone.checked = true;
        clone.setAttribute('checked', ''); // keep attribute + property in sync
      } else {
        clone.removeAttribute('checked');
      }
    });
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        callback(html);
      })
      .then(() => {
        document.querySelector(`#${targetId}`)?.focus();
      })
      .catch((error) => {
        // Clear the swapping flag so a failed/aborted fetch doesn't leave the
        // product area dimmed permanently (this outgoing node stays mounted when
        // no swap happens).
        this.removeAttribute('data-swapping');
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode
      .querySelector('variant-selects [data-selected-variant]')
      ?.textContent?.trim();
    if (!selectedVariant || selectedVariant === 'null') return null;
    try { return JSON.parse(selectedVariant); } catch { return null; }
  }

  buildSwapRequestUrl(productUrl, handle) {
    const selectedSize = ProductInfo.getSelectedSizeValue(this);
    const entry = ProductInfo.getEntryByHandle(this, handle);
    const sizeValues = ProductInfo.getSizeValues(entry);
    const target = selectedSize ? sizeValues?.[selectedSize] : null;

    if (target?.available && target.variantUrl) {
      return `${target.variantUrl}&section_id=${this.sectionId}`;
    }
    return `${productUrl}?section_id=${this.sectionId}`;
  }

  updateVariantInputs(variantId) {
    this.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    ).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variantId ?? '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateURL(url, variantId) {
    if (this.dataset.updateUrl === 'false') return;
    if (!ProductInfo.getSelectedSizeValue(this)) return;
    window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

    const selectors = ['price', 'Sku']
      .map((id) => `#${id}-${this.dataset.section}`)
      .join(', ');
    document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
  }

  updateMedia(variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return;
    this.scrollsnap?.scrollToElementById(variantFeaturedMediaId);
  }

  updateNostoPrices() {
    function waitForElement(selector, callback) {
      const observer = new MutationObserver((mutations, observer) => {
        const element = document.querySelector(selector);
        if (element) {
            observer.disconnect();
            callback(element);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    function removeTrailingZeros(priceString) {
      const number = parseFloat(priceString.replace(/[^0-9.-]+/g, ''));
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(number);
    }

    waitForElement('.featured-collection-section', (element) => {
      const nostoPrices = element.querySelectorAll('.nosto-price');
      nostoPrices.forEach(price => {
        price.textContent = removeTrailingZeros(price.textContent);
      });
    });
  }

  get productForm() {
    return this.querySelector('product-form');
  }

  get variantSelectors() {
    return this.querySelector('variant-selects');
  }

  get sectionId() {
    return this.dataset.originalSection || this.dataset.section;
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}

// Backward-compatible alias for quick-add.js and other external consumers.
window.PdpSizeSelection = {
  EMPTY_SIZE_LABEL,
  getLandedEntry: (pi) => ProductInfo.getLandedEntry(pi),
  getEntryByHandle: (pi, h) => ProductInfo.getEntryByHandle(pi, h),
  getSizeOptionKey: (e) => ProductInfo.getSizeOptionKey(e),
  getSizeValues: (e) => ProductInfo.getSizeValues(e),
  getSizeFieldset: (pi) => ProductInfo.getSizeFieldset(pi),
  getSizeInput: (fs, v) => ProductInfo.getSizeInput(fs, v),
  getSelectedSizeValue: (pi) => ProductInfo.getSelectedSizeValue(pi),
  resolveSelectedVariant: (pi) => ProductInfo.resolveSelectedVariant(pi),
  setSizeLabel: (pi, v) => ProductInfo.setSizeLabel(pi, v),
  setPendingSizeSelection: (pi, v) => ProductInfo.setPendingSizeSelection(pi, v),
  reapplySizeAfterVariantUpdate: (pi) => ProductInfo.reapplySizeAfterVariantUpdate(pi),
  syncSubmitButton: (pi) => ProductInfo.syncSubmitButton(pi),
  getMainProductInfo: () => document.querySelector('product-info[id^="MainProduct"]'),
};
